import React, { useCallback, useEffect, useState } from "react";
import { api } from "../api/client";
import { formatKarachiDate } from "../utils/dateUtils";
import { GENDER_BG_COLORS } from "./AdminDashboard";

const TABS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "schedule", label: "Schedule" },
  { key: "settings", label: "Settings" },
];

const timePattern = "(0d|[0-9]|1d|2[0-3]):([0-5]d)";
const timeRegex = /^(0\d|[0-9]|1\d|2[0-3]):([0-5]\d)$/;

export default function UserDashboard() {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [schedule, setSchedule] = useState([]);
  const [user, setUser] = useState(null);
  const [trips, setTrips] = useState([]);
  const [payment, setPayment] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState([]);
  const [isTemporary, setIsTemporary] = useState(false);
  const [pwForm, setPwForm] = useState({
    oldPassword: "",
    newPassword: "",
    confirm: "",
  });
  const [pwMsg, setPwMsg] = useState("");
  const [announcements, setAnnouncements] = useState([]);

  const fetchUser = async () => {
    const me = await api("/auth/me");
    setUser(me.user);
    setSchedule(me.user.schedule);
    setForm(me.user.schedule.map((d) => ({ ...d })));
  };
  const fetchTrips = async () => {
    const t = await api("/trips/mine");
    setTrips(t.trips);
  };
  const fetchPayment = async () => {
    const pay = await api("/payments/status");
    setPayment(pay);
  };
  const fetchAnnouncements = async () => {
    try {
      const res = await api("/trips/announcements/passenger");
      setAnnouncements(res.announcements);
    } catch (err) {
      console.error("Failed to fetch announcements");
    }
  };
  const load = useCallback(async () => {
    await fetchUser();
    await fetchTrips();
    await fetchPayment();
    await fetchAnnouncements();
  }, []);
  
  useEffect(() => {
    load();
  }, [load]);

  const requestChange = async () => {
    const changes = form.filter((d, i) => {
      const orig = schedule[i];
      return (
        d.morning !== orig.morning ||
        d.evening !== orig.evening ||
        d.off !== orig.off
      );
    });
    if (changes.length === 0) {
      alert("No changes detected");
      cancelScheduleEdit();
      return;
    }

    for (let change of changes) {
      if (!change.day) {
        alert("Please fill in all fields");
        return;
      }
      if (!change.off && change.morning && !timeRegex.test(change.morning)) {
        alert("Invalid morning time format");
        return;
      }
      if (!change.off && change.evening && !timeRegex.test(change.evening)) {
        alert("Invalid evening time format");
        return;
      }
    }

    try {
      await api("/schedule/request-change", {
        method: "POST",
        body: { changes, isTemporary },
      });
      alert("Change request sent");
      setEdit(false);
      setIsTemporary(false);
      load();
    } catch (err) {
      alert(err.message || "Failed to request change");
    }
  };
  const cancelScheduleEdit = () => {
    setEdit(false);
    setIsTemporary(false);
    setForm(schedule.map((d) => ({ ...d })));
  };
  const updatePassword = async (e) => {
    e.preventDefault();
    setPwMsg("");
    if (
      !pwForm.oldPassword ||
      !pwForm.newPassword ||
      pwForm.newPassword.length < 6 ||
      pwForm.newPassword !== pwForm.confirm
    ) {
      setPwMsg("Invalid input or passwords do not match");
      return;
    }
    try {
      await api("/auth/update-password", {
        method: "POST",
        body: {
          oldPassword: pwForm.oldPassword,
          newPassword: pwForm.newPassword,
        },
      });
      setPwMsg("Password updated successfully");
      setPwForm({ oldPassword: "", newPassword: "", confirm: "" });
    } catch (err) {
      setPwMsg(err.message);
    }
  };

  return (
    <div>
      <nav className="user-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`tab-btn${activeTab === tab.key ? " active" : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </nav>
      <div className="tab-content">
        {activeTab === "dashboard" && (
          <div className="card">
            {announcements.length > 0 && (
              <div style={{ marginBottom: "1.5rem" }}>
                {announcements.map((announcement) => (
                  <div
                    key={announcement._id}
                    style={{
                      padding: "1rem",
                      background: "#fef3c7",
                      border: "2px solid #f59e0b",
                      borderRadius: "8px",
                      marginBottom: "0.75rem",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    <strong style={{ color: "#92400e" }}>📢 Announcement:</strong>
                    <p style={{ margin: "0.5rem 0 0 0", color: "#78350f" }}>{announcement.message}</p>
                  </div>
                ))}
              </div>
            )}
            <h3>Upcoming Trips</h3>

            <div className="upcoming-trips">
              {trips.length > 0 ? (
                trips.map((trip) => {
                  const formattedDate = formatKarachiDate(trip.date, {
                    weekday: "long",
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  });
                  
                  const timestampLabel = trip.updatedAt && trip.updatedAt !== trip.createdAt ? "Updated At" : "Created At";
                  const timestampValue = trip.updatedAt && trip.updatedAt !== trip.createdAt ? trip.updatedAt : trip.createdAt;
                  const formattedTimestamp = timestampValue ? formatKarachiDate(timestampValue, {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  }) : "";
                  
                  return (
                    <div key={trip._id} className="day-block">
                      <h2 className="day-heading">{formattedDate}</h2>
                      <div className="trip-grid">
                        <div className={`trip-card ${trip.type}`}>
                          <p className="badge">
                            {trip.type.toString().toUpperCase()}
                          </p>
                          <p className="car-info">
                            {trip.car?.name} ({trip.car?.number})
                          </p>
                          {formattedTimestamp && (
                            <p style={{ fontSize: "0.75rem", color: "#9ca3af", margin: "0.5rem 0 0 0" }}>
                              {timestampLabel}: {formattedTimestamp}
                            </p>
                          )}
                          {trip.car?.note && (
                            <p className="trip-note" style={{ 
                              marginTop: "0.5rem", 
                              fontSize: "0.875rem", 
                              color: "#374151",
                              whiteSpace: "pre-wrap",
                              background: "#f3f4f6",
                              padding: "0.5rem",
                              borderRadius: "4px"
                            }}>
                              {trip.car.note}
                            </p>
                          )}
                          {trip.isCoordinator && trip.passengers && (
                            <div style={{ marginTop: "1rem" }}>
                              <p style={{ 
                                fontWeight: "bold", 
                                fontSize: "0.875rem", 
                                lineHeight: "1.5rem",
                                marginBottom: "0.5rem",
                                color: "#000000"
                              }}>
                                You are a coordinator. 
                                Please review the passenger list carefully.
                              </p>
                              <div className="passenger-list scroll-x">
                                <table className="table table-list" style={{ backgroundColor: "#ffffff" }}>
                                  <thead>
                                    <tr>
                                      <th>Sr#</th>
                                      <th>Name</th>
                                      <th>Stop</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {trip.passengers.map((p, index) => (
                                      <tr key={p._id}>
                                        <td>{index + 1}</td>
                                        <td style={{
                                          backgroundColor: GENDER_BG_COLORS[p.gender] || "#ffffff"
                                        }}>{p.name}</td>
                                        <td style={{ backgroundColor: p.stopColor || "transparent" }}>{p.stop?.name || "N/A"}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="no-trips">No upcoming trips</p>
              )}
            </div>

            <h4 style={{ marginTop: 20 }}>Payment Status</h4>
            {payment && payment.payments && payment.payments.length > 0 ? (
              <div>
                {payment.hasOverdue && (
                  <div
                    style={{
                      padding: "1rem",
                      background: "#fef2f2",
                      border: "1px solid #ef4444",
                      borderRadius: 8,
                      color: "#b91c1c",
                      marginBottom: 16,
                    }}
                  >
                    <strong>
                      You have {payment.overduePayments.length} overdue
                      payment(s)
                    </strong>
                  </div>
                )}
                {payment.upcomingPayment && (
                  <div
                    style={{
                      padding: "1rem",
                      background:
                        payment.upcomingPayment.status === "overdue"
                          ? "#fef2f2"
                          : "#f0f9ff",
                      border:
                        payment.upcomingPayment.status === "overdue"
                          ? "1px solid #ef4444"
                          : "1px solid #3b82f6",
                      borderRadius: 8,
                      marginBottom: 16,
                    }}
                  >
                    <h5 style={{ margin: 0, marginBottom: 8 }}>
                      {payment.upcomingPayment.status === "overdue"
                        ? "Overdue Payment"
                        : "Upcoming Payment"}
                    </h5>
                    <p style={{ margin: 0, fontSize: "0.875rem" }}>
                      <strong>Amount:</strong> PKR{" "}
                      {payment.upcomingPayment.amount}
                    </p>
                    <p style={{ margin: 0, fontSize: "0.875rem" }}>
                      <strong>Due Date:</strong>{" "}
                      {formatKarachiDate(payment.upcomingPayment.dueDate, {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </p>
                    <p style={{ margin: 0, fontSize: "0.875rem" }}>
                      <strong>Status:</strong>{" "}
                      <span
                        className={`badge ${
                          payment.upcomingPayment.status === "paid"
                            ? "approved"
                            : payment.upcomingPayment.status === "overdue"
                            ? "rejected"
                            : "pending"
                        }`}
                      >
                        {payment.upcomingPayment.status}
                      </span>
                    </p>
                  </div>
                )}
                <h5>Recent Payment History</h5>
                <div className="scroll-x">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th>Amount</th>
                        <th>Due Date</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payment.payments.slice(0, 6).map((p) => (
                        <tr key={p._id}>
                          <td>{p.monthYear}</td>
                          <td>PKR {p.amount}</td>
                          <td>
                            {formatKarachiDate(p.dueDate, {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })}
                          </td>
                          <td>
                            <span
                              className={`badge ${
                                p.status === "paid"
                                  ? "approved"
                                  : p.status === "overdue"
                                  ? "rejected"
                                  : "pending"
                              }`}
                            >
                              {p.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <p style={{ fontSize: "0.875rem", color: "#6b7280" }}>
                No payment information available. Please contact admin.
              </p>
            )}
          </div>
        )}
        {activeTab === "schedule" && (
          <div className="card">
            <h3>Schedule</h3>
            {!edit && (
              <button className="btn" onClick={() => setEdit(true)}>
                Edit
              </button>
            )}
            {edit && (
              <div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    className="btn"
                    onClick={requestChange}
                    type="button"
                    style={{ background: "#555" }}
                  >
                    Request Change
                  </button>
                  <button
                    className="btn"
                    onClick={cancelScheduleEdit}
                    type="button"
                    style={{ background: "#999" }}
                  >
                    Cancel
                  </button>
                </div>
                <div style={{ marginBottom: 12, marginTop: 24 }}>
                  <label style={{ display: "flex", alignItems: "center" }}>
                    <input
                      type="checkbox"
                      checked={isTemporary}
                      onChange={(e) => setIsTemporary(e.target.checked)}
                    />
                    <span>Temporary Change (will revert after 24 hours)</span>
                  </label>
                </div>
              </div>
            )}
            <div className="scroll-x">
              <table className="table" style={{ marginTop: 10 }}>
                <thead>
                  <tr>
                    <th>Day</th>
                    <th>Off</th>
                    <th>Morning</th>
                    <th>Evening</th>
                    <th>Requested</th>
                    <th>Temporary Change</th>
                  </tr>
                </thead>
                <tbody>
                  {form.map((d, i) => (
                    <tr key={d.day}>
                      <td>{d.day}</td>
                      <td>
                        <input
                          type="checkbox"
                          checked={!!d.off}
                          onChange={(e) => {
                            if (!edit) return;
                            const v = e.target.checked;
                            setForm((f) =>
                              f.map((x, j) =>
                                j === i
                                  ? {
                                      ...x,
                                      off: v,
                                      ...(v
                                        ? { morning: "", evening: "" }
                                        : {}),
                                    }
                                  : x
                              )
                            );
                          }}
                          disabled={!edit}
                        />
                      </td>
                      <td>
                        <input
                          value={d.morning || ""}
                          onChange={(e) => {
                            if (!edit) return;
                            const v = e.target.value;
                            setForm((f) =>
                              f.map((x, j) =>
                                j === i ? { ...x, morning: v } : x
                              )
                            );
                          }}
                          placeholder={d.off ? "Day is off" : "HH:mm (24-hr)"}
                          disabled={!edit || d.off}
                          pattern={timePattern}
                          style={{
                            borderColor:
                              d.morning && !timeRegex.test(d.morning)
                                ? "red"
                                : undefined,
                                ...(d.off ? { backgroundColor: "#c2c2c2", cursor: "not-allowed" } : {}),
                          }}
                          title={
                            d.morning && !timeRegex.test(d.morning)
                              ? "Format must be HH:mm (e.g. 08:30)"
                              : ""
                          }
                        />
                        {d.morning && !timeRegex.test(d.morning) && (
                          <span
                            style={{
                              color: "red",
                              fontSize: "0.8em",
                            }}
                          >
                            Invalid time
                          </span>
                        )}
                      </td>
                      <td>
                        <input
                          value={d.evening || ""}
                          onChange={(e) => {
                            if (!edit) return;
                            const v = e.target.value;
                            setForm((f) =>
                              f.map((x, j) =>
                                j === i ? { ...x, evening: v } : x
                              )
                            );
                          }}
                          placeholder={d.off ? "Day is off" : "HH:mm (24-hr)"}
                          disabled={!edit || d.off}
                          pattern={timePattern}
                          style={{
                            borderColor:
                              d.evening && !timeRegex.test(d.evening)
                                ? "red"
                                : undefined,
                                ...(d.off ? { backgroundColor: "#c2c2c2", cursor: "not-allowed" } : {}),
                          }}
                          title={
                            d.evening && !timeRegex.test(d.evening)
                              ? "Format must be HH:mm (e.g. 08:30)"
                              : ""
                          }
                        />
                        {d.evening && !timeRegex.test(d.evening) && (
                          <span
                            style={{
                              color: "red",
                              fontSize: "0.8em",
                            }}
                          >
                            Invalid time
                          </span>
                        )}
                      </td>

                      <td>
                        {d.pendingChange && d.requested
                          ? d.requested.isTemporary
                            ? "Temporary"
                            : "Permanent"
                          : "-"}
                      </td>
                      <td>
                        {d.temporaryChange ? (
                          <span style={{ fontSize: "0.85em" }}>
                            Reverts:{" "}
                            {new Date(
                              d.temporaryChange.revertAt
                            ).toLocaleString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {activeTab === "settings" && (
          <div className="card">
            <h3>Account Settings</h3>
            <p>
              <i>You can not update some settings. Ask admin for help.</i>
            </p>
            <div className="scroll-x">
              <table className="table">
                <tbody>
                  <tr>
                    <td>Name</td>
                    <td>{user ? user.name : "-"}</td>
                  </tr>
                  <tr>
                    <td>Email</td>
                    <td>{user ? user.email : "-"}</td>
                  </tr>
                  <tr>
                    <td>Gender</td>
                    <td>
                      {user && user.gender
                        ? user.gender.charAt(0).toUpperCase() +
                          user.gender.slice(1)
                        : "-"}
                    </td>
                  </tr>
                  <tr>
                    <td>Contact</td>
                    <td>{user && user.contact ? user.contact : "-"}</td>
                  </tr>
                  <tr>
                    <td>Stop</td>
                    <td>
                      {user && user.stop ? user.stop.name || user.stop : "-"}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <h4 style={{ marginTop: 18 }}>Update Password</h4>
            <form style={{ maxWidth: 400 }} onSubmit={updatePassword}>
              <label>Old Password</label>
              <input
                type="password"
                value={pwForm.oldPassword}
                onChange={(e) =>
                  setPwForm((f) => ({ ...f, oldPassword: e.target.value }))
                }
                required
              />
              <label>New Password</label>
              <input
                type="password"
                value={pwForm.newPassword}
                onChange={(e) =>
                  setPwForm((f) => ({ ...f, newPassword: e.target.value }))
                }
                required
                minLength={6}
              />
              <label>Confirm New Password</label>
              <input
                type="password"
                value={pwForm.confirm}
                onChange={(e) =>
                  setPwForm((f) => ({ ...f, confirm: e.target.value }))
                }
                required
                minLength={6}
              />
              <button
                className="btn success"
                type="submit"
                style={{ marginTop: 10 }}
              >
                Update Password
              </button>
              {pwMsg && (
                <div
                  style={{
                    color: pwMsg.includes("success") ? "green" : "red",
                    marginTop: 8,
                  }}
                >
                  {pwMsg}
                </div>
              )}
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
